import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../services/api"; // Ensure this imports the configured axios instance
import toast from "react-hot-toast";

interface User {
  id: string; // Keep this for compatibility, but map from _id
  _id?: string; // Add the MongoDB field
  company_name: string;
  email: string;
  mobile: string;
  customer_type: string;
  gstNo: string;
  tally_serialno: string;
  tally_version: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  tempToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  rememberMe: boolean;
  login: (
    credentials: LoginCredentials
  ) => Promise<{ success: boolean; requiresVerification?: boolean }>;
  verify2FA: (code: string) => Promise<boolean>;
  resendCode: () => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  invalidateAllTokens: () => Promise<boolean>;
  resetPassword: (details: {
    email: string;
    mobile: string;
    gstNo: string;
    tally_serialno: string;
  }) => Promise<boolean>;
  confirmResetPassword: (
    token: string,
    newPassword: string
  ) => Promise<boolean>;
}

interface LoginCredentials {
  email: string;
  password: string;
  mobile: string;
  gstNo: string;
  tally_serialno: string;
  rememberMe?: boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      tempToken: null,
      isAuthenticated: false,
      isLoading: false,
      rememberMe: false,
      login: async (credentials) => {
        set({ isLoading: true, rememberMe: credentials.rememberMe || false });
        try {
          const response = await api.post("/auth/login", credentials);
          const { tempToken, message } = response.data;
          if (tempToken) {
            set({
              tempToken,
              isLoading: false,
              user: { email: credentials.email } as User,
            });
            return { success: true, requiresVerification: true };
          }
          // Tally serial mismatch case
          if (message || message === "") {
            toast.error(message); // Show toast error
            set({ isLoading: false });
            return { success: false, message }; // Return message for further logic
          }
          // Handle unknown response
          toast.error("Unexpected response");
          set({ isLoading: false });
          return { success: false, message: "Unexpected response" };
        } catch (error: any) {
          set({ isLoading: false });
          const message = error.response?.data?.message || "Login failed";
          toast.error(message);
          return { success: false, message };
        }
      },
      verify2FA: async (code) => {
        const { tempToken, rememberMe } = get();
        if (!tempToken) return false;
        set({ isLoading: true });
        try {
          const response = await api.post("/auth/verify-2fa", {
            tempToken,
            verificationCode: code,
          });
          // Map MongoDB _id to id for compatibility
          const userData = response.data.user;
          const mappedUser = {
            ...userData,
            id: userData._id || userData.id,
          };
          set({
            user: mappedUser,
            token: response.data.token,
            tempToken: null,
            isAuthenticated: true,
            isLoading: false,
            rememberMe,
          });
          toast.success("Login successful!");
          return true;
        } catch (error: any) {
          set({ isLoading: false });
          toast.error(error.response?.data?.message || "Verification failed");
          return false;
        }
      },
      resendCode: async () => {
        const { tempToken, user } = get();
        const email = user?.email;
        if (!tempToken || !email) {
          toast.error("Email or temp token is missing");
          return false;
        }
        try {
          await api.post("/auth/resend-verification", {
            tempToken,
          });
          toast.success("Verification code resent!");
          return true;
        } catch (error: any) {
          toast.error(error.response?.data?.message || "Failed to resend code");
          return false;
        }
      },
      logout: () => {
        console.log("[AuthStore] Performing logout...");
        set({
          user: null,
          token: null,
          tempToken: null,
          isAuthenticated: false,
          rememberMe: false,
        });
        toast.success("Logged out successfully");
      },
      refreshToken: async () => {
        console.log("[AuthStore] Attempting to refresh token...");
        try {
          const response = await api.post("/auth/refresh-token");
          set({ token: response.data.token });
          console.log("[AuthStore] Token refreshed successfully.");
          return true;
        } catch (error: any) {
          console.error(
            "[AuthStore] Refresh token failed:",
            error.response?.data || error.message
          );
          // The interceptor will catch this 200 from /auth/refresh-token and handle logout
          // We don't need to call get().logout() here directly, as it would be redundant
          // and could lead to multiple logout calls/toasts.
          return false;
        }
      },
      invalidateAllTokens: async () => {
        try {
          await api.post("/auth/logout");
          get().logout();
          toast.success("Logged out successfully");
          return true;
        } catch (error: any) {
          toast.error("Failed to logout");
          return false;
        }
      },
      resetPassword: async ({ email, mobile, gstNo, tally_serialno }) => {
        if (get().isLoading) return false;
        set({ isLoading: true });
        try {
          await api.post("users/forgot-password", {
            email,
            mobile,
            gstNo,
            tally_serialno,
          });
          toast.success("Password reset email sent!");
          return true;
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to send reset email"
          );
          return false;
        } finally {
          set({ isLoading: false });
        }
      },
      confirmResetPassword: async (token, newPassword) => {
        if (get().isLoading) return false;
        set({ isLoading: true });
        try {
          await api.post("/users/reset-password", { token, newPassword });
          toast.success("Password reset successfully!");
          return true;
        } catch (error: any) {
          toast.error(
            error.response?.data?.message || "Failed to reset password"
          );
          return false;
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
      // Only persist if rememberMe is true
      skipHydration: false,
    }
  )
);
